/**
 * Nettside (2026-10-20): what the mosque's own website needs to show prayer times, the programme and notices from
 * the platform — the WordPress plugin, the API address it is set to, and the website key.
 *
 * The key is made by the server (POST /admin/website/key), shown ONCE in this page's state, and never readable
 * again: a new one replaces it. With the key the API counts upload and status limits per visitor rather than per
 * website (every visitor of a WordPress site arrives from ONE server address). Admin tier: settings.write.
 */
import React, { useState } from 'react';
import { download, errText, post, send, type Body } from '../lib/api';
import { useLoad, useMsg } from '../lib/hooks';
import { Btn, Card, CopyField, LoadError, Loading, Msg, Tag, TwoStep } from '../components/ui';
import { SocialConnect } from '../components/SocialConnect';
import { YouTubeConnect } from '../components/YouTube';
import { useNav } from '../lib/nav';

const SHORTCODES: [string, string][] = [
  ['[masjid_bonnetider]', 'dagens bønnetider, Begynner og Jamaat'],
  ['[masjid_neste_jamaat]', 'bare neste jamaat, til toppen av forsiden'],
  ['[masjid_uke]', 'ukens jamaat-tider, mandag først'],
  ['[masjid_program antall="3" detalj="/arrangement/"]', 'kommende arrangementer, med lenke til en side som har [masjid_arrangement]'],
  ['[masjid_varsler]', 'varselstripe, inkludert janazah'],
  ['[masjid_janazah]', 'bare janazah-stripen (til toppen av hver side; tom når det ikke er noen)'],
  ['[masjid_skjema type="nikah|omvisning|fatiha|kontakt"]', 'skjemaene, rett inn i Henvendelser her i panelet'],
];

const DESIGNS: [string, string][] = [
  ['[masjid_bonnetider mal="…"]', 'tabell (standard) · kort · stripe · stor'],
  ['[masjid_neste_jamaat mal="…"]', 'standard · kort · linje'],
  ['[masjid_program mal="…"]', 'liste (standard) · kort (med bilde) · kompakt'],
  ['[masjid_uke mal="…"]', 'tabell (standard) · kompakt (for mobil)'],
  ['[masjid_varsler mal="…"]', 'standard · kort'],
];

export function Nettside() {
  const { data, error, reload } = useLoad<Body>('/website');
  // Facebook and Instagram (2026-10-23): only where the mosque has direct publishing.
  const { caps } = useNav();
  const social = Boolean(caps && caps.includes('social_meta'));
  // A3 (2026-10-24): the mosque's YouTube channel, where the Facebook connection lives.
  const youtube = Boolean(caps && caps.includes('youtube_live_auto'));
  const [msg, say] = useMsg();
  const [busy, setBusy] = useState(false);
  const [fresh, setFresh] = useState<string | null>(null);
  if (error) return <LoadError error={error} retry={reload} />;
  if (!data) return <Loading />;
  const key = data.key || {};

  const makeKey = async () => {
    setBusy(true);
    const r = await post('/website/key');
    setBusy(false);
    if (!r.ok) return say(errText(r.body), 'err');
    setFresh(r.body.key);
    say(r.body.message_no || 'Nettstedsnøkkel laget.', 'ok');
    reload();
  };
  const removeKey = async () => {
    setBusy(true);
    const r = await send('/website/key', { method: 'DELETE' });
    setBusy(false);
    if (!r.ok) return say(errText(r.body), 'err');
    setFresh(null);
    say(r.body.message_no || 'Fjernet.', 'ok');
    reload();
  };
  const getZip = async () => {
    setBusy(true);
    const r = await download('/v1/admin/website/plugin.zip', 'masjid-platform.zip');
    setBusy(false);
    if (!r.ok) say(r.message || 'Fikk ikke lastet ned programtillegget.', 'err');
  };

  return (
    <>
      {social && <SocialConnect />}
      {youtube && <YouTubeConnect />}
      <Card title="Nettsiden henter fra Masjid" hint="Bønnetider, program og varsler på moskeens egen nettside kommer herfra. Endres en tid her i panelet, endres den på nettsiden — uten at noen logger inn i WordPress.">
        <Msg text={msg.text} tone={msg.tone} />
        <CopyField label="API-adresse (limes inn i WordPress › Innstillinger › Min Masjid)" value={String(data.apiBase || '')} />
      </Card>

      <Card title="Nettstedsnøkkel" hint="Nøkkelen limes inn som «API-nøkkel» i programtillegget. Med den teller plattformen grensene for opplastinger og statusoppslag per besøkende; uten den deler hele nettsiden én grense.">
        <p className="field note" style={{ margin: '6px 18px' }}>
          Status: {key.set
            ? <><Tag tone="green">satt</Tag>{key.source === 'platform' ? <span className="note"> · satt av Masjid i serverens innstillinger; en nøkkel laget her erstatter den for denne moskeen</span> : null}</>
            : <Tag tone="grey">ingen nøkkel</Tag>}
        </p>
        {fresh && (
          <div role="status" style={{ margin: '6px 18px' }}>
            <CopyField label="Nøkkelen — vises bare nå. Lim den inn i WordPress før du forlater siden." value={fresh} />
            <p className="field note">Nøkkelen kan ikke hentes fram igjen. Mister dere den, lag en ny her: den gamle slutter da å virke.</p>
          </div>
        )}
        {!data.onPlatform
          ? <p className="field note" style={{ margin: '6px 18px' }}>På denne installasjonen settes nøkkelen i serverens innstillinger (MASJID_SITE_KEYS), ikke her.</p>
          : <div className="row" style={{ margin: '6px 18px', gap: 8 }}>
              {key.source === 'mosque'
                ? <>
                    <TwoStep label="Lag ny nøkkel" busyLabel="Lager …" disabled={busy} confirmLabel="Klikk igjen: nettsiden slutter å virke med den gamle nøkkelen til den nye er limt inn" onConfirm={makeKey} />
                    <TwoStep label="Fjern nøkkelen" busyLabel="Fjerner …" disabled={busy} confirmLabel="Klikk igjen: nettsiden deler da én grense for opplastinger og statusoppslag" onConfirm={removeKey} />
                  </>
                : <Btn disabled={busy} onClick={makeKey}>{busy ? 'Lager …' : 'Lag nettstedsnøkkel'}</Btn>}
            </div>}
      </Card>

      <Card title="Programtillegg for WordPress" hint="Last ned, legg inn under Utvidelser › Legg til ny › Last opp, aktiver, og lim inn API-adressen og nøkkelen under Innstillinger › Min Masjid.">
        <div className="row" style={{ margin: '6px 18px' }}>
          {data.plugin
            ? <Btn disabled={busy} onClick={getZip}>Last ned masjid-platform.zip</Btn>
            : <span className="note">Programtillegget er ikke med i denne utgaven av serveren. Be Masjid om filen.</span>}
        </div>
        <p className="field note" style={{ margin: '6px 18px' }}>Kortkoder (de gamle <code>[cjas_…]</code>-navnene virker fortsatt):</p>
        <table className="t">
          <thead><tr><th scope="col">Kortkode</th><th scope="col">Viser</th></tr></thead>
          <tbody>{SHORTCODES.map(([code, what]) => <tr key={code}><td><code>{code}</code></td><td>{what}</td></tr>)}</tbody>
        </table>
        <p className="field note" style={{ margin: '6px 18px' }}>
          Større tekst eller fet skrift på enhver kortkode: <code>storrelse="125"</code> (prosent, 50–300; eller <code>liten</code>, <code>stor</code>, <code>større</code>, <code>størst</code>)
          og <code>fet="ja"</code>. For eksempel <code>[masjid_bonnetider storrelse="stor" fet="ja"]</code>.
        </p>
        {/* Designs per block (owner, 2026-10-25): plugin 0.3.0, CJAS_Designs. In WordPress' block editor the same choice is
            «Design» in the block's sidebar. */}
        <p className="field note" style={{ margin: '6px 18px' }}>Velg design med <code>mal="…"</code> (eller «Design» i blokkens sidepanel i WordPress):</p>
        <table className="t">
          <thead><tr><th scope="col">Kortkode</th><th scope="col">Design</th></tr></thead>
          <tbody>{DESIGNS.map(([code, list]) => <tr key={code}><td><code>{code}</code></td><td>{list}</td></tr>)}</tbody>
        </table>
      </Card>
    </>
  );
}
